import React, { useEffect, useRef, useState } from 'react';

import CanvasWorkspace from './CanvasWorkspace';
import BilingualInvoiceRenderer from './BilingualInvoiceRenderer';
import {
  buildCwElements,
  LineItem,
  TaxLine,
  DiscountLine,
} from './QuickInvoiceView';

import { ActiveTemplate, PageSizePreset } from '../types';
import { amountToWords } from '../utils/numberToWords';

type MobileInvoiceData = {
  template: any;
  lineItems: LineItem[];
  taxLines: TaxLine[];
  discountLines: DiscountLine[];
  shippingCost: number;
  estimatedTableHeight?: number;
  invoiceDate?: string;
  invoiceTime?: string;

  clientName: string;
  clientEmail: string;
  clientPhone: string;
  clientAddress: string;

  notes: string;
  currency: string;
  zoom?: number;
  cashierName?: string;
  cashierNameTranslit?: string;
  invoiceNumber?: string;
  sellerCountry?: string;
  sellerRegion?: string;
  buyerRegion?: string;
  buyerTaxId?: string;
  isB2B?: boolean;
  taxScheme?: any;
  taxBreakdown?: any[];
  customColumns?: any[];

  showInvoiceQR?: boolean;
showTrackingQR?: boolean;
autoQrPosition?: 'left' | 'center' | 'right';
autoQrSize?: number;
autoQrLabelSize?: number;

showAmountInWords?: boolean;

// POS / Quick Invoice bilingual preview
bilingualMode?: boolean;
translateTargetLang?: string;
translatedCart?: Record<string | number, string>;

// Full BilingualInvoiceRenderer preview data
invoiceStatus?: string;
bilingualLayout?: 'side' | 'stacked';
amountInWordsTranslated?: string;
notesTranslated?: string;
taxBreakdownAr?: Record<string, string>;
qrCaptionInvoiceAr?: string;
qrCaptionTrackingAr?: string;

amountInWordsStyle?: {
  fontFamily: string;
  fontSize: number;
  textAlign: 'left' | 'center' | 'right';
  fontStyle: 'normal' | 'italic';
  fontWeight: 'normal' | 'bold';
  color: string;
};
};

export default function MobileInvoicePreview() {
  // ⚡ 0ms Initialization: Load from pre-injected window variable immediately on Frame 1
  const [data, setData] = useState<MobileInvoiceData | null>(() => {
    if (typeof window !== 'undefined' && (window as any).__INITIAL_INVOICE_DATA__) {
      return (window as any).__INITIAL_INVOICE_DATA__;
    }
    return null;
  });

  const canvasRef = useRef<HTMLDivElement>(null);
  const [gestureZoom, setGestureZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [qrOffset, setQrOffset] = useState({ x: 0, y: 0 });
  const [measuredTableHeight, setMeasuredTableHeight] = useState(0);

const panRef = useRef({
  x: 0,
  y: 0,
});

const gestureRef = useRef({
  startX: 0,
  startY: 0,
  startPanX: 0,
  startPanY: 0,
  startDistance: 0,
  startZoom: 1,

  draggingQr: false,
  startQrX: 0,
  startQrY: 0,
});

const getTouchDistance = (touches: React.TouchList) => {
  if (touches.length < 2) return 0;

  const dx = touches[0].clientX - touches[1].clientX;
  const dy = touches[0].clientY - touches[1].clientY;

  return Math.sqrt(dx * dx + dy * dy);
};

const handleGestureStart = (e: React.TouchEvent) => {
  const target = e.target as HTMLElement;
  try {
    (window as any).ReactNativeWebView?.postMessage(
      JSON.stringify({ type: 'TOUCH_DEBUG', touches: e.touches.length })
    );
  } catch {}

  const touchedElement = target.closest('[data-elid]');
  const touchedQr =
    touchedElement?.getAttribute('data-elid') === 'preview-invoice-qr';

  gestureRef.current.draggingQr = touchedQr;

  if (e.touches.length === 1) {
    gestureRef.current.startX = e.touches[0].clientX;
    gestureRef.current.startY = e.touches[0].clientY;

    gestureRef.current.startPanX = pan.x;
gestureRef.current.startPanY = pan.y;
if (touchedQr) {
  gestureRef.current.startQrX = qrOffset.x;
  gestureRef.current.startQrY = qrOffset.y;
}
  }

  if (e.touches.length === 2) {
    gestureRef.current.startDistance = getTouchDistance(e.touches);
    gestureRef.current.startZoom = gestureZoom;
  }
};

const handleGestureMove = (e: React.TouchEvent) => {
  e.preventDefault();
  e.stopPropagation();

  // One finger → drag QR OR pan invoice
if (e.touches.length === 1) {
  const dx = e.touches[0].clientX - gestureRef.current.startX;
  const dy = e.touches[0].clientY - gestureRef.current.startY;

  // Finger started on QR → move only QR
  if (gestureRef.current.draggingQr) {
    setQrOffset({
      x: gestureRef.current.startQrX + dx / gestureZoom,
      y: gestureRef.current.startQrY + dy / gestureZoom,
    });

    return;
  }

  // Move ONLY the invoice freely.
// Background stays fixed.
const nextX = gestureRef.current.startPanX + dx;
const nextY = gestureRef.current.startPanY + dy;

panRef.current.x = nextX;
panRef.current.y = nextY;

setPan({
  x: nextX,
  y: nextY,
});
}

  // Two fingers → pinch zoom
  if (
    e.touches.length === 2 &&
    gestureRef.current.startDistance > 0
  ) {
    const distance = getTouchDistance(e.touches);

    const nextZoom = Math.max(
      0.3,
      Math.min(
        3,
        gestureRef.current.startZoom *
          (distance / gestureRef.current.startDistance)
      )
    );

    setGestureZoom(nextZoom);
  }
};

const handleGestureEnd = () => {
  gestureRef.current.startDistance = 0;
  gestureRef.current.draggingQr = false;
};

const [fitZoomToken, setFitZoomToken] = useState(0);

const handleResetView = () => {
  // Don't force zoom to a flat 1 — bump the token so the child
  // recomputes a fit-to-screen zoom (A4/Letter is wider than the
  // phone screen; only thermal pages actually fit at 100%).
  setFitZoomToken(t => t + 1);
  setPan({ x: 0, y: 0 });
  setQrOffset({ x: 0, y: 0 });

  panRef.current.x = 0;
  panRef.current.y = 0;
};
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Ignore anything that isn't a plain JSON object string — the same
      // window 'message' channel also carries unrelated dev-tooling
      // traffic (Expo Dev Client / Metro inspector bridge, etc.), which
      // is never JSON and isn't meant for us. Only attempt to parse
      // messages that look like our own payloads.
      if (typeof event.data !== 'string') return;
      const trimmed = event.data.trim();
      if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return;

      let message: any;
      try {
        message = JSON.parse(trimmed);
      } catch {
        // Not one of ours — silently ignore, don't log.
        return;
      }

      if (message?.type === 'INVOICE_UPDATE') {
        console.log('[MobileInvoicePreview] received lineItems:', message.payload?.lineItems);
        console.log('[MobileInvoicePreview] discountLines:', JSON.stringify(message.payload?.discountLines));
        setData(message.payload);
      }

      if (
        message?.type === 'SET_INVOICE_ZOOM' &&
        typeof message.zoom === 'number'
      ) {
        setGestureZoom(
          Math.max(0.3, Math.min(3, message.zoom))
        );
      }

      if (message?.type === 'RESET_INVOICE_VIEW') {
        handleResetView();
      }
    };

    window.addEventListener('message', handleMessage);
    document.addEventListener('message', handleMessage as any);

    // Handshake: tell React Native the listener is attached and ready for data
    try {
      (window as any).ReactNativeWebView?.postMessage(
        JSON.stringify({ type: 'PREVIEW_READY' })
      );
    } catch {}

    return () => {
      window.removeEventListener('message', handleMessage);
      document.removeEventListener('message', handleMessage as any);
    };
  }, []);

  useEffect(() => {
  if (!data) return;

  const measureTable = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const tableEl = canvas.querySelector(
      '[data-elid]'
    ) as HTMLElement | null;

    const actualTable = Array.from(
      canvas.querySelectorAll('[data-elid]')
    ).find((node: any) => {
      const id = node.getAttribute('data-elid');
      const source = data.template?.elements?.find(
        (el: any) => el.id === id
      );

      return source?.type === 'table';
    }) as HTMLElement | undefined;

    if (!actualTable) return;

    /*
     * offsetHeight is in the unscaled canvas coordinate system.
     * Do NOT use getBoundingClientRect().height because preview zoom
     * would contaminate the measurement.
     */
    const height = actualTable.offsetHeight;

    if (height > 0) {
      setMeasuredTableHeight(prev =>
        Math.abs(prev - height) > 1 ? height : prev
      );
    }
  };

  const raf1 = requestAnimationFrame(() => {
    const raf2 = requestAnimationFrame(measureTable);

    (window as any).__mobileInvoiceMeasureRaf2 = raf2;
  });

  const observer = new ResizeObserver(measureTable);

  const timer = window.setTimeout(() => {
    const canvas = canvasRef.current;

    if (canvas) {
      canvas
        .querySelectorAll('[data-elid]')
        .forEach(node => observer.observe(node));
    }

    measureTable();
  }, 50);

  return () => {
    cancelAnimationFrame(raf1);

    if ((window as any).__mobileInvoiceMeasureRaf2) {
      cancelAnimationFrame(
        (window as any).__mobileInvoiceMeasureRaf2
      );
    }

    clearTimeout(timer);
    observer.disconnect();
  };
}, [
  data,
  gestureZoom,
]);

  if (!data) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f1f5f9',
          fontFamily: 'Arial, sans-serif',
          color: '#64748b',
        }}
      >
        Waiting for invoice data...
      </div>
    );
  }

  return (
    <MobileInvoicePreviewContent
      data={data}
      gestureZoom={gestureZoom}
      setGestureZoom={setGestureZoom}
      pan={pan}
      qrOffset={qrOffset}
      measuredTableHeight={measuredTableHeight}
      canvasRef={canvasRef}
      handleGestureStart={handleGestureStart}
      handleGestureMove={handleGestureMove}
      handleGestureEnd={handleGestureEnd}
      handleResetView={handleResetView}
      fitZoomToken={fitZoomToken}
    />
  );
}

export function MobileInvoicePreviewContent({
  data,
  gestureZoom,
  setGestureZoom,
  pan,
  qrOffset,
  measuredTableHeight,
  canvasRef,
  handleGestureStart,
  handleGestureMove,
  handleGestureEnd,
  handleResetView,
  fitZoomToken,
}: {
  data: MobileInvoiceData;
  gestureZoom: number;
  setGestureZoom: (z: number) => void;
  pan: { x: number; y: number };
  qrOffset: { x: number; y: number };
  measuredTableHeight: number;
  canvasRef: React.RefObject<HTMLDivElement>;
  handleGestureStart: (e: React.TouchEvent) => void;
  handleGestureMove: (e: React.TouchEvent) => void;
  handleGestureEnd: () => void;
  handleResetView: () => void;
  fitZoomToken: number;
}) {
  /*
   * Mobile/backend templates don't necessarily use the exact same
   * property names as ActiveTemplate.
   *
   * Normalize them here so the existing web renderer can consume them.
   */
  const rawTemplate = data.template || {};

  const pageSize = (
    rawTemplate.pageSize ||
    rawTemplate.page_size ||
    rawTemplate.layout?.page_size ||
    'A4'
  ) as PageSizePreset;

  const rawElements =
    rawTemplate.elements ||
    rawTemplate.layout?.elements ||
    [];

  const isThermal = pageSize === 'THERMAL_58MM' || pageSize === 'THERMAL_80MM';

  // 1. Remove empty image placeholders & giant blank shapes on thermal receipts
  const sanitizedElements = rawElements.filter((el: any) => {
    if (!el || typeof el !== 'object') return false;

    // Filter out image blocks without a valid source URL (removes the solid purple block)
    if (el.type === 'image' || el.type === 'logo') {
      const src = el.src || el.url || el.dataUrl || el.imageUrl;
      if (!src || typeof src !== 'string' || !src.trim() || src.trim() === 'undefined' || src.trim() === 'null') {
        return false;
      }
    }

    // On thermal only: strip empty desktop shapes (>40px tall with no text)
    if (
      isThermal &&
      (el.type === 'shape' || el.type === 'rect' || el.type === 'rectangle' || el.type === 'box')
    ) {
      const hasText = Boolean(el.textContent?.trim() || el.text?.trim());
      if (Number(el.height || 0) > 40 && !hasText) {
        return false;
      }
    }

    return true;
  });

  // 2. Pull remaining receipt content up to the top so there is no dead space
  const minY = sanitizedElements.length > 0
    ? Math.min(...sanitizedElements.map((el: any) => Number(el.y || 0)))
    : 0;

  const shouldShiftUp = isThermal && minY > 45;
  const shiftAmount = shouldShiftUp ? minY - 10 : 0;

  // ── Format country date live for preview (DD/MM/YYYY for IN/UK/AE, MM/DD/YYYY for US) ──
  const country = ((data as any).sellerCountry || (data as any).posOutletCountry || (data as any).qiOutletCountry || 'IN').toUpperCase();
  const formatLiveCountryDate = (rawDate?: string) => {
    let d: Date;
    if (rawDate && rawDate.includes('-')) {
      const [y, m, day] = rawDate.split('-').map(Number);
      d = new Date(y, m - 1, day);
    } else if (rawDate) {
      d = new Date(rawDate);
    } else {
      d = new Date();
    }
    if (isNaN(d.getTime())) d = new Date();
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return country === 'US' ? `${month}/${day}/${year}` : `${day}/${month}/${year}`;
  };

  const liveCountryDate = formatLiveCountryDate(data.invoiceDate);
  const liveCashierName = ((data as any).cashierName || (data as any).serverName || 'Staff').trim();
  const liveInvoiceNumber = (data as any).invoiceNumber || '';
  const liveClientDetails = (data as any).clientName || 'Walk-in Customer';

  // ⚡ Detect table header theme color directly from the template (Red #b91c1c, Purple #7c3aed, etc.)
  const tableElSource = sanitizedElements.find((e: any) => e.type === 'table');
  const tableThemeColor =
    tableElSource?.headerBackground ||
    tableElSource?.headerBg ||
    tableElSource?.columns?.[0]?.headerBackground ||
    tableElSource?.columns?.[0]?.headerBg ||
    '#b91c1c';

  const finalElements = sanitizedElements.map((el: any) => {
    let patchedEl = { ...el };

    // ⚡ Safeguard: Never allow store headers, restaurant info, or KOT meta to become a customer card
    const isStoreHeader = Boolean(
      patchedEl.textContent && (
        /\b(gstin|fssai|tax invoice|store|retail|shop|restaurant|cuisine|table:\s*\d|kot:)\b/i.test(patchedEl.textContent) ||
        patchedEl.textContent.split('\n').length > 5
      )
    );

    if (isStoreHeader) {
      if (patchedEl.id === 'auto-injected-client-box') patchedEl.id = 'store-header-box';
      delete patchedEl.backgroundColor;
      delete patchedEl.background;
      delete patchedEl.borderColor;
      delete patchedEl.borderLeft;
      delete patchedEl.borderLeftWidth;
      delete patchedEl.borderLeftColor;
    }

    // ⚡ Customer card styling should ONLY apply to Thermal receipts
    const isCustomerCard = isThermal && patchedEl.id === 'auto-injected-client-box' && !isStoreHeader;

    if (isCustomerCard) {
      patchedEl.id = 'auto-injected-client-box';
      patchedEl.backgroundColor = '#f8fafc';
      patchedEl.borderColor = '#e2e8f0';
      patchedEl.borderLeftColor = tableThemeColor;
      patchedEl.borderLeftWidth = 4;
      patchedEl.borderLeft = `4px solid ${tableThemeColor}`;
      patchedEl.borderWidth = 1;
      patchedEl.borderRadius = 4;
      patchedEl.padding = 6;
      patchedEl.fontFamily = 'monospace';
      patchedEl.color = '#0f172a';

      if (patchedEl.textContent) {
        patchedEl.textContent = patchedEl.textContent.replace(/\bNPWP:\b/g, 'Tax ID:');
      }
    } else if (!isThermal) {
      // Strip any injected card borders & backgrounds from A4/Letter
      delete patchedEl.backgroundColor;
      delete patchedEl.background;
      delete patchedEl.borderColor;
      delete patchedEl.borderLeft;
      delete patchedEl.borderLeftWidth;
      delete patchedEl.borderLeftColor;
      delete patchedEl.borderWidth;
      delete patchedEl.borderRadius;
      delete patchedEl.padding;
    }

    // ⚡ Live-patch Signature elements (both English and Bilingual)
    if (patchedEl.type === 'signature') {
      if (liveCashierName) {
        patchedEl.signatureName = (patchedEl.signatureName || '{staff_name}').replace(
          /\{staff_name\}/gi,
          liveCashierName
        );
        patchedEl.arabicSignatureName = (patchedEl.arabicSignatureName || (data as any).cashierNameTranslit || liveCashierName).replace(
          /\{staff_name\}/gi,
          (data as any).cashierNameTranslit || liveCashierName
        );
      }
      return patchedEl;
    }

    // ⚡ Live-patch Server, Date, Client, and Invoice Number in text elements
    if (patchedEl.type === 'text' && patchedEl.textContent) {
      let t = patchedEl.textContent;

      // 1. Live Server / Cashier replacement
      if (liveCashierName) {
        t = t.replace(
          /\b(Server|Cashier|Waiter|Barista|Pharmacist|Stylist|Staff|Operator|Host|Captain):\s*[\w\s\.]*?(?=\s{2,}\S+:|\n|$)/gi,
          (_m: string, label: string) => `${label}: ${liveCashierName}`
        );
        t = t.replace(/\{staff_name\}/gi, liveCashierName);
      }

      // 2. Live Country Date replacement (preserves Due Date)
      if (liveCountryDate) {
        t = t.replace(/(?<!Due\s)\bDate:\s*[\d]{1,4}[\/\-.][\d]{1,4}[\/\-.][\d]{2,4}/gi, `Date: ${liveCountryDate}`);
        t = t.replace(/\{date\}/gi, liveCountryDate);
      }

      // 3. Live Invoice / Bill Number replacement
      if (liveInvoiceNumber) {
        t = t.replace(/\{invoice_number\}/gi, liveInvoiceNumber);
        t = t.replace(/\b[A-Z]{2,4}-(?:[A-Z]{2,4}-)?[\d]{3,}(?:-[\d]+)*\b/g, liveInvoiceNumber);
        t = t.replace(/\bBill:\s*[\w\-\d]+/gi, `Bill: ${liveInvoiceNumber}`);
        t = t.replace(/\bInvoice:\s*[\w\-\d]+/gi, `Invoice: ${liveInvoiceNumber}`);
      }

      // 4. Live Client Details replacement
      t = t.replace(/\{client_details\}/gi, liveClientDetails);

      patchedEl.textContent = t;

      if (patchedEl.arabicContent) {
        let arT = patchedEl.arabicContent;
        if (liveCashierName) {
          arT = arT.replace(
            /\b(Server|Cashier|Waiter|Barista|Pharmacist|Stylist|Staff|Operator|Host|Captain):\s*[\w\s\.]*?(?=\s{2,}\S+:|\n|$)/gi,
            (_m: string, label: string) => `${label}: ${(data as any).cashierNameTranslit || liveCashierName}`
          );
        }
        if (liveCountryDate) {
          arT = arT.replace(/(?<!Due\s)\bDate:\s*[\d]{1,4}[\/\-.][\d]{1,2}[\/\-.][\d]{2,4}/gi, `Date: ${liveCountryDate}`);
          arT = arT.replace(/\{date\}/gi, liveCountryDate);
        }
        if (liveInvoiceNumber) {
          arT = arT.replace(/\bPENDING\b/g, liveInvoiceNumber);
          arT = arT.replace(/\{invoice_number\}/gi, liveInvoiceNumber);
          arT = arT.replace(/\b[A-Z]{2,4}-(?:[A-Z]{2,4}-)?[\d]{3,}(?:-[\d]+)*\b/g, liveInvoiceNumber);
        }
        patchedEl.arabicContent = arT;
      }
    }

    // Preserve exact template designer coordinates on A4 sheets
    if (shiftAmount > 0) {
      return { ...patchedEl, y: Math.max(0, Number(patchedEl.y || 0) - shiftAmount) };
    }
    return patchedEl;
  });

  const template: ActiveTemplate = {
    ...rawTemplate,
    name: rawTemplate.name || 'Invoice',
    pageSize,
    elements: finalElements,
    layout: rawTemplate.layout
      ? { ...rawTemplate.layout, elements: finalElements }
      : { elements: finalElements },
  };

  // A4/Letter/Legal/A5 are wider than a phone screen at 100% zoom, so
  // the right edge gets clipped (thermal pages are already narrow
  // enough to fit as-is). Auto-scale to fit the actual WebView width
  // — window.innerWidth here is the real device viewport. Only
  // recompute when the page size changes or an explicit reset is
  // requested, so it never fights the user's own pinch/+/- zoom.
  const lastFitKeyRef = useRef<string>('');
  useEffect(() => {
    const pageWidthPx =
      pageSize === 'THERMAL_58MM' ? 220 :
      pageSize === 'THERMAL_80MM' ? 302 :
      pageSize === 'A5' ? 559 :
      pageSize === 'LETTER' || pageSize === 'LEGAL' ? 816 :
      794; // A4
    const key = `${pageSize}-${fitZoomToken}`;
    if (lastFitKeyRef.current === key) return;
    lastFitKeyRef.current = key;

    const screenWidth = window.innerWidth || 400;
    const fit = Math.min(1, (screenWidth - 16) / pageWidthPx);
    setGestureZoom(Math.max(0.3, parseFloat(fit.toFixed(2))));
  }, [pageSize, fitZoomToken]);





const invoiceDate = data.invoiceDate || new Date().toISOString().slice(0, 10);
const invoiceTime = data.invoiceTime || new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  // On A4 invoices, don't force extra blank rows if the footer already has signatures, stamps, or QRs
  const isA4 = !isThermal;
  const rawLineItems = (data.lineItems && data.lineItems.length > 0 && data.lineItems.some(li => li.description?.trim()))
    ? data.lineItems
    : [{ id: 'sample-row', description: 'Sample Item / Service', qty: 1, rate: 0, amount: 0 }];

  const hasHeavyFooter = sanitizedElements.some((el: any) =>
    el.type === 'signature' || el.type === 'stamp' || (el.type === 'qr' && !el._auto)
  );

  // If there's a stamp/signature/QR below, keep the table compact so it fits on Page 1!
  const MIN_A4_ITEMS = hasHeavyFooter ? 2 : 4;
  let effectiveLineItems = rawLineItems;

  if (isA4 && rawLineItems.length < MIN_A4_ITEMS) {
    const padCount = MIN_A4_ITEMS - rawLineItems.length;
    effectiveLineItems = [
      ...rawLineItems,
      ...Array.from({ length: padCount }, (_, i) => ({
        id: `empty-pad-${i}`,
        description: '\u00A0',
        qty: '' as any,
        rate: '' as any,
        amount: '' as any,
      })),
    ];
  }

   const cwData = buildCwElements(template, {
    lineItems: effectiveLineItems,
    taxLines: data.taxLines || [],
    discountLines: data.discountLines || [],
    shippingCost: Number(data.shippingCost || 0),
    clientName: data.clientName || '',
    clientEmail: data.clientEmail || '',
    clientPhone: data.clientPhone || '',
    clientAddress: data.clientAddress || '',
    buyerTaxId: data.buyerTaxId,

    notes: data.notes || '',
    cashierName: data.cashierName || '',
    customColumns: (data as any).customColumns || [],

    invoiceDate,
    dueDate: '',
    showDueDate: false,

    showInvoiceQR: data.showInvoiceQR ?? true,

    autoQrPosition: data.autoQrPosition || 'center',
    autoQrSize: data.autoQrSize ?? 100,
    autoQrLabelSize: data.autoQrLabelSize ?? 12,
    showAmountInWords: data.showAmountInWords ?? false,

amountInWordsStyle: data.amountInWordsStyle || {
  fontFamily: 'Inter, Arial, sans-serif',
  fontSize: 9,
  textAlign: 'center',
  fontStyle: 'normal',
  fontWeight: 'normal',
  color: '#475569',
},
    autoQrOffset: {
      x: 0,
      y: 0,
    },

    qrValue: `${window.location.origin}/?i=preview`,

    translatedValues: Object.fromEntries(
  (data.lineItems || []).map((item) => [
    `item_${item.id}`,
    item.translatedDescription ||
      data.translatedCart?.[item.id] ||
      ''
  ])
),
  });



  const previewAmountInWordsElements: any[] = [];

// Shared table measurements.
// Amount in Words, QR and all other below-table content can use
// the exact same table bottom.
const tableElement = cwData.cwElements.find(
  (el: any) => el.type === 'table'
);

const pageWidth = cwData.pageConfig.width;
const pageScale = Math.min(1, pageWidth / 794);

// ⚡ Define storedTableHeight so originalTableBottom down below doesn't throw a ReferenceError
const storedTableHeight = Number(tableElement?.height || 0);

// ⚡ Calculate real dynamic table height based on actual items (removes the 230px phantom gap!)
const activeItemsCount = Math.max(1, (data.lineItems || []).filter((li: any) => li.description?.trim()).length);
const activeTaxesCount = (data.taxLines || []).filter((t: any) => Number(t.rate) > 0).length;
const activeDiscsCount = (data.discountLines || []).filter((d: any) => Number(d.rate) > 0 || Number(d.amount) > 0).length;

const rowH = Boolean(data.bilingualMode) ? 36 : 24;
const headerH = 34;
const totalsH = 36 + (activeTaxesCount + activeDiscsCount + (Number(data.shippingCost) > 0 ? 1 : 0)) * 18;
const computedTableHeight = headerH + (activeItemsCount * rowH) + totalsH;

// ⚡ Use the accurate computedTableHeight directly (removes the 250px gap!)
const realTableHeight = tableElement
  ? (measuredTableHeight > 0 ? measuredTableHeight : computedTableHeight)
  : 0;

const isThermalPreview = pageWidth <= 302;
const tableStartY = tableElement ? Number(tableElement.y || 0) : 0;

// ⚡ Table bottom strictly hugs the actual items
const tableBottom = tableElement
  ? (tableStartY + realTableHeight)
  : cwData.cwElements.reduce((max: number, el: any) => Math.max(max, (Number(el.y) || 0) + (Number(el.height) || 0)), 0);

// Define originalTableBottom so no reference error is ever thrown!
const originalTableBottom = tableBottom;

let shiftedCwElements;

if (isThermalPreview && tableElement) {
  const footerElements = cwData.cwElements
    .filter((el: any) => el.type !== 'table' && (el.y || 0) > tableStartY + 40)
    .sort((a: any, b: any) => (a.y || 0) - (b.y || 0));

  const FOOTER_START_GAP = pageWidth <= 220 ? 10 : 12;
  const FOOTER_ITEM_GAP = pageWidth <= 220 ? 6 : 8;

  // If "Amount in Words" is on, it renders right under the table (see
  // block below) — footer items must start AFTER it, or they stack
  // on top of it like in the overlap bug.
  const amountWordsGap = Math.max(4, 8 * pageScale);
  const amountWordsHeight = data.showAmountInWords
    ? Math.max(30, (data.amountInWordsStyle?.fontSize || 9) * 3)
    : 0;
  const footerStartY = data.showAmountInWords
    ? tableBottom + amountWordsGap + amountWordsHeight
    : tableBottom;

  const footerY = new Map<string, number>();
  let cursor = footerStartY + FOOTER_START_GAP;

  footerElements.forEach((el: any, idx: number) => {
    const originalHeight = Math.max(1, Number(el.height || 0));
    if (idx > 0) {
      // Always use the fixed small gap — never preserve the template's
      // original spacing. A template designed for more sample rows can
      // have huge baked-in gaps between footer elements; those must be
      // fully collapsed, not just floored, for a tight thermal receipt flow.
      cursor += FOOTER_ITEM_GAP;
    }
    footerY.set(el.id, cursor);
    cursor += originalHeight;
  });

  shiftedCwElements = cwData.cwElements.map((el: any) => {
    if (el.type === 'table') return el;
    return footerY.has(el.id) ? { ...el, y: footerY.get(el.id) } : el;
  });
} else {
  // ⚡ A4/Letter: Catch ALL elements below the table (signature, stamp, Net30 QR) and hug the table!
  const footerElements = cwData.cwElements.filter(
    (el: any) => el.type !== 'table' && (el.y || 0) > tableStartY + 40
  );

  const minFooterY = footerElements.length > 0
    ? Math.min(...footerElements.map((el: any) => Number(el.y || 0)))
    : tableBottom;

  const targetFooterStartY = tableBottom + (data.showAmountInWords ? 42 : 18);
  const footerShift = targetFooterStartY - minFooterY;

  shiftedCwElements = cwData.cwElements.map((el: any) => {
    if (el.type === 'table') return el;
    if ((el.y || 0) > tableStartY + 40) {
      return { ...el, y: Math.round((el.y || 0) + footerShift) };
    }
    return el;
  });
}

if (data.showAmountInWords) {

  const subtotal = (data.lineItems || []).reduce(
    (sum: number, li: any) =>
      sum +
      (Number(li.qty) || 0) *
      (Number(li.rate) || 0),
    0
  );

  const tax = (data.taxLines || []).reduce(
    (sum: number, t: any) =>
      sum + subtotal * ((Number(t.rate) || 0) / 100),
    0
  );

  const discount = (data.discountLines || []).reduce(
    (sum: number, d: any) =>
      sum + subtotal * ((Number(d.rate) || 0) / 100),
    0
  );

  const total =
    subtotal +
    tax -
    discount +
    Number(data.shippingCost || 0);

  const style = data.amountInWordsStyle || {
    fontFamily: 'Inter',
    fontSize: 9,
    textAlign: 'center' as const,
    fontStyle: 'normal' as const,
    fontWeight: 'normal' as const,
    color: '#475569',
  };

  previewAmountInWordsElements.push({
    id: 'mobile-preview-amount-in-words',
    type: 'text',

    x: 20,
    y: tableBottom + Math.max(4, 8 * pageScale),

    width: Math.max(100, cwData.pageConfig.width - 40),
    height: Math.max(30, style.fontSize * 3),

    zIndex: 998,
    opacity: 100,
    rotation: 0,

    textContent: `Amount in Words: ${amountToWords(
      total,
      data.currency || 'INR'
    )}`,

    fontFamily: style.fontFamily,
    fontSize: style.fontSize,
    textAlign: style.textAlign,
    fontStyle: style.fontStyle,
    fontWeight: style.fontWeight,
    color: style.color,
  });
}

// ⚡ 1. Identify legacy placeholder invoice QRs without touching template payment QRs (Net30 / UPI)
  const isPlaceholderInvoiceQr = (qrEl: any) =>
    shiftedCwElements.some(
      (other: any) =>
        other.type === 'text' &&
        other.textContent?.trim().toLowerCase().includes('scan to view invoice') &&
        Math.abs((Number(other.y) || 0) - ((Number(qrEl.y) || 0) + (Number(qrEl.height) || 0))) < 50
    );

  // ⚡ 2. Filter out old auto-invoice QRs or placeholders — preserve template payment QRs (Net30)
  shiftedCwElements = shiftedCwElements.filter((el: any) => {
    if (el._auto === 'invoice' || el._auto === 'invoice_label') return false;
    if (
      el.id &&
      (String(el.id).startsWith('auto-invoice-qr') ||
        String(el.id).startsWith('preview-invoice-qr') ||
        String(el.id).startsWith('attached-invoice-qr'))
    ) {
      return false;
    }
    if (el.type === 'qr' && isPlaceholderInvoiceQr(el)) return false;
    if (el.type === 'text' && el.textContent?.trim().toLowerCase() === 'scan to view invoice') return false;
    return true;
  });

  const previewQrElements: any[] = [];

  const invCapEn = 'Scan to view invoice';
  // ⚡ Universal caption for all 100+ languages (no hardcoded Arabic fallback!)
  const invCapAr =
    data.qrCaptionInvoiceAr ||
    (data as any).translatedValues?.qr_caption_invoice ||
    '';

  // ⚡ Resolve real invoice view URL using public_token or invoiceQR from backend
  const publicToken =
    (data as any).publicToken ||
    (data as any).public_token ||
    (data as any).invoice?.public_token ||
    '';

  const realInvoiceUrl =
    (data as any).invoiceQR ||
    (data as any).invoice_qr ||
    (publicToken ? `https://cloudairambo.com/invoice/view/${publicToken}` : '') ||
    (data.invoiceNumber && data.invoiceNumber !== 'PREVIEW' ? `https://cloudairambo.com/invoice/view/${data.invoiceNumber}` : '') ||
    `${window.location.origin}/?i=preview`;

  if (data.showInvoiceQR ?? true) {
    const qrSize = data.autoQrSize ?? 100;
    const qrPosition = (data.autoQrPosition || (data as any).qr_position || (data as any).qrPosition || 'center').toLowerCase();
    const qrLabelSize = data.autoQrLabelSize ?? 10;

    // Measure the bottom of all elements (table, notes, terms, and template Net30 QR)
    const contentBottom = [
      ...shiftedCwElements,
      ...previewAmountInWordsElements,
    ].reduce(
      (max: number, el: any) => Math.max(max, (Number(el.y) || 0) + (Number(el.height) || 0)),
      0
    );

    const safeContentBottom = Math.max(
      contentBottom,
      previewAmountInWordsElements.length > 0
        ? Math.max(...previewAmountInWordsElements.map((el: any) => (el.y || 0) + (el.height || 0)))
        : 0
    );

    // ⚡ Compact gap so the QR doesn't push past the 1123px A4 page height
    const gap = isThermal ? (pageWidth <= 220 ? 8 : 10) : 10;
    const y = safeContentBottom + gap + qrOffset.y;

    // Align X with the table bounds (matching Web Quick Invoice)
    const tableEl = shiftedCwElements.find((e: any) => e.type === 'table');
    const tableX = Number(tableEl?.x) || (isThermal ? 10 : 30);
    const tableW = Number(tableEl?.width) || (pageWidth - (isThermal ? 20 : 60));

    const baseX =
      qrPosition === 'left'
        ? tableX
        : qrPosition === 'right'
        ? tableX + tableW - qrSize
        : tableX + (tableW - qrSize) / 2;

    const x = Math.max(0, Math.min(pageWidth - qrSize, baseX + qrOffset.x));

    // Center caption snugly 3px under QR code
    const labelW = Math.max(qrSize + 40, 120);
    const labelX = x + (qrSize - labelW) / 2;
    const labelY = y + qrSize + 3;

    previewQrElements.push({
      id: 'preview-invoice-qr',
      type: 'qr',
      _auto: 'invoice',
      x: Math.round(x),
      y: Math.round(y),
      width: qrSize,
      height: qrSize,
      zIndex: 999,
      opacity: 100,
      rotation: 0,
      qrValue: realInvoiceUrl, // 👈 Uses the real public invoice URL!
    });

    previewQrElements.push({
      id: 'preview-invoice-qr-label',
      type: 'text',
      _auto: 'invoice_label',
      x: Math.round(labelX),
      y: Math.round(labelY),
      width: Math.round(labelW),
      // ⚡ Only print translated subtitle if bilingualMode is actually ON!
      height: (Boolean(data.bilingualMode) && invCapAr) ? 36 : 18,
      zIndex: 999,
      opacity: 100,
      rotation: 0,
      textContent: (Boolean(data.bilingualMode) && invCapAr) ? `${invCapEn}\n${invCapAr}` : invCapEn,
      arabicContent: (Boolean(data.bilingualMode) && invCapAr) ? invCapAr : undefined,
      fontSize: qrLabelSize,
      textAlign: 'center',
      color: '#000000',
      fontWeight: '800',
      fontFamily: 'Inter',
    });
  }

  // ⚡ REAL TRACKING QR RENDERER: Always shows whenever showTrackingQR is ON!
  const orderTok = (data as any).orderToken || (data as any).order_token || (data as any).translatedValues?.order_token || (data as any).activeOrderToken || '';
  const wantsTracking = Boolean(data.showTrackingQR || (data as any).show_tracking_qr || (data as any).showTrackingPlaceholder);

  // ⚡ Filter out any duplicate or old auto-tracking elements so they never collide
  shiftedCwElements = shiftedCwElements.filter(
    (el: any) => el._auto !== 'tracking' && el._auto !== 'tracking_label' && !String(el.id || '').startsWith('preview-tracking')
  );

  if (wantsTracking) {
    const qrSize = data.autoQrSize ?? 100;
    const qrLabelSize = data.autoQrLabelSize ?? 10;
    const trackingTokenVal = orderTok || 'preview';
    const trackingUrl = `https://cloudairambo.com/wait?token=${trackingTokenVal}`;
    const trackCapTr = data.qrCaptionTrackingAr || (data as any).translatedValues?.qr_caption_tracking || '';

    // Calculate Y matching the invoice QR
    const tableEl = shiftedCwElements.find((e: any) => e.type === 'table');
    const tableX = Number(tableEl?.x) || (isThermal ? 10 : 30);
    const tableW = Number(tableEl?.width) || (pageWidth - (isThermal ? 20 : 60));

    const contentBottom = [
      ...shiftedCwElements,
      ...previewAmountInWordsElements,
    ].reduce(
      (max: number, el: any) => Math.max(max, (Number(el.y) || 0) + (Number(el.height) || 0)),
      0
    );

    const safeBottom = Math.max(
      contentBottom,
      previewAmountInWordsElements.length > 0
        ? Math.max(...previewAmountInWordsElements.map((el: any) => (el.y || 0) + (el.height || 0)))
        : 0
    );

    const gap = isThermal ? (pageWidth <= 220 ? 8 : 10) : 10;
    
    // On Thermal: stack tracking QR below invoice QR. On A4: put side-by-side!
    let trackX: number;
    let trackY: number;

    if (isThermal) {
      trackX = Math.round((pageWidth - qrSize) / 2);
      // Place 45px below invoice QR label
      trackY = safeBottom + gap + qrSize + 40 + qrOffset.y;
    } else {
      // A4: Place Tracking QR on the opposite side of Invoice QR
      const invPos = (data.autoQrPosition || (data as any).qr_position || 'center').toLowerCase();
      if (invPos === 'right') {
        trackX = tableX; // Track on Left, Invoice on Right
      } else {
        trackX = tableX + tableW - qrSize; // Track on Right, Invoice on Left/Center
      }
      trackY = safeBottom + gap + qrOffset.y;
    }

    const labelW = Math.max(qrSize + 40, 140);
    const labelX = trackX + (qrSize - labelW) / 2;
    const labelY = trackY + qrSize + 3;

    previewQrElements.push({
      id: 'preview-tracking-qr',
      type: 'qr',
      _auto: 'tracking',
      x: Math.round(trackX),
      y: Math.round(trackY),
      width: qrSize,
      height: qrSize,
      zIndex: 999,
      opacity: 100,
      rotation: 0,
      qrValue: trackingUrl,
    });

    previewQrElements.push({
      id: 'preview-tracking-qr-label',
      type: 'text',
      _auto: 'tracking_label',
      x: Math.round(labelX),
      y: Math.round(labelY),
      width: Math.round(labelW),
      height: (Boolean(data.bilingualMode) && trackCapTr) ? 36 : 18,
      zIndex: 999,
      opacity: 100,
      rotation: 0,
      textContent: (Boolean(data.bilingualMode) && trackCapTr) 
        ? `Scan to track order\n${trackCapTr}` 
        : `Scan to track order${orderTok ? ` (${orderTok})` : ''}`,
      arabicContent: (Boolean(data.bilingualMode) && trackCapTr) ? trackCapTr : undefined,
      fontSize: qrLabelSize,
      textAlign: 'center',
      color: '#000000',
      fontWeight: '800',
      fontFamily: 'Inter',
    });
  }

const allPreviewElements = [
  ...shiftedCwElements,
  ...previewAmountInWordsElements,
  ...previewQrElements,
];

const previewContentBottom = allPreviewElements.reduce(
  (max: number, el: any) =>
    Math.max(max, (Number(el.y) || 0) + (Number(el.height) || 0)),
  0
);

// ⚡ Dynamic clean height calculation (Zero missing variables!)
const computedHeight = isThermalPreview
  ? Math.max(200, previewContentBottom + 20)
  : Math.max(1123, previewContentBottom + 20);


  useEffect(() => {
    (window as any).ReactNativeWebView?.postMessage(
      JSON.stringify({
        type: 'CONTENT_HEIGHT',
        height: Math.ceil(computedHeight),
        width: cwData.pageConfig.width,
      })
    );
  }, [computedHeight, cwData.pageConfig.width]);

  const qrElementsForRender = React.useMemo(
    () => [...shiftedCwElements, ...previewAmountInWordsElements, ...previewQrElements].filter(
      (el: any) => el.type === 'qr' && el.qrValue
    ),
    [shiftedCwElements, previewAmountInWordsElements, previewQrElements]
  );
  const qrSignature = qrElementsForRender.map((el: any) => `${el.id}:${el.qrValue}`).join('|');
  const [renderedQrDataUrls, setRenderedQrDataUrls] = useState<Record<string, string>>({});
  useEffect(() => {
    if (qrElementsForRender.length === 0) return;
    let cancelled = false;
    (async () => {
      const QRCode = await import('qrcode');
      const entries = await Promise.all(
        qrElementsForRender.map(async (el: any) => [
          el.id,
          await QRCode.default.toDataURL(el.qrValue, { width: 200, margin: 1 }),
        ] as [string, string])
      );
      if (!cancelled) setRenderedQrDataUrls(Object.fromEntries(entries));
    })();
    return () => { cancelled = true; };
  }, [qrSignature]);

  const computeTableTotals = (el: any) => {
    let subtotal = 0;
    const lineBases: { base: number; taxRate: number }[] = [];

    (el.rows || []).forEach((row: any) => {
      const qty = parseFloat(row.values?.qty) || 0;
      const rate = parseFloat(row.values?.rate) || 0;
      const lineTotal = qty * rate;
      const taxRate = parseFloat(row.values?.taxRate ?? row.values?.tax_rate ?? 0) || 0;

      if (taxRate <= 0) {
        subtotal += lineTotal;
        lineBases.push({ base: lineTotal, taxRate: 0 });
        return;
      }

      const inclusive = !!(row.values?.taxInclusive ?? row.values?.tax_inclusive);
      if (inclusive) {
        const base = lineTotal / (1 + taxRate / 100);
        subtotal += base;
        lineBases.push({ base, taxRate });
      } else {
        subtotal += lineTotal;
        lineBases.push({ base: lineTotal, taxRate });
      }
    });

    // 1. Total Discounts
    const discount = (el.discounts || []).reduce(
      (sum: number, d: any) =>
        sum + (d.amount !== undefined ? Number(d.amount) : subtotal * ((Number(d.rate) || 0) / 100)),
      0
    );

    // 2. Discount reduces the taxable base before product tax
    const discountRatio = subtotal > 0 ? Math.min(1, discount / subtotal) : 0;
    const productTax = lineBases.reduce((sum, lb) => {
      const discountedBase = lb.base * (1 - discountRatio);
      return sum + (discountedBase * lb.taxRate / 100);
    }, 0);

    const taxableValue = subtotal - discount;

    // 3. Manual Tax Lines on taxable value
    const tax = (el.taxes || []).reduce(
      (sum: number, taxLine: any) =>
        sum + taxableValue * ((Number(taxLine.rate) || 0) / 100),
      0
    );

    // 4. Shipping is added at the end (not taxed by product tax)
    const shipping = Number(el.shippingCost || 0);
    const finalTotal = Math.round((taxableValue + productTax + tax + shipping + Number.EPSILON) * 100) / 100;

    return {
      subtotal,
      tax,
      productTax,
      discount,
      shipping,
      taxBreakdown: (data as any).taxBreakdown,
      total: finalTotal,
    };
  };

  // ⚡ Live-format customer card: ONLY runs on Thermal receipts!
  useEffect(() => {
    if (!isThermal) return;
    const formatCustomerCards = () => {
      // 1. Detect table header background color from the live table
      let barColor = '';
      const allHeaders = document.querySelectorAll('th, tr, div');
      for (let i = 0; i < allHeaders.length; i++) {
        const txt = (allHeaders[i].textContent || '').toLowerCase().trim();
        if ((txt === 'item' || txt.startsWith('item ') || txt.startsWith('आइटम')) && allHeaders[i].children.length === 0) {
          let n: HTMLElement | null = allHeaders[i] as HTMLElement;
          for (let k = 0; k < 5 && n && n !== document.body; k++) {
            const bg = window.getComputedStyle(n).backgroundColor;
            if (bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent' && bg !== 'rgb(255, 255, 255)') {
              barColor = bg;
              break;
            }
            n = n.parentElement;
          }
          if (barColor) break;
        }
      }

      // Fallback to template table background if DOM color wasn't caught
      if (!barColor) {
        const tbl = data.template?.elements?.find((e: any) => e.type === 'table');
        barColor = tbl?.headerBackground || tbl?.headerBg || '#b91c1c';
      }

      // 2. Select ONLY the real customer card (never the header)
      let cards: HTMLElement[] = Array.from(
        document.querySelectorAll('[data-elid="auto-injected-client-box"], #auto-injected-client-box')
      ) as HTMLElement[];

      // Fallback for bilingual renderer (STRICT: height < 140px, no parent walking)
      if (!cards.length) {
        const candidateDivs = document.querySelectorAll('.mobile-invoice-canvas div');
        for (let i = 0; i < candidateDivs.length; i++) {
          const div = candidateDivs[i] as HTMLElement;
          if (div.offsetHeight > 140 || div.offsetHeight < 35) continue;
          if (div.querySelector('table')) continue;
          const text = (div.textContent || '').trim();
          if (/(restaurant|store|fssai|table:|covers:|kot:|bill of supply)/i.test(text)) continue;
          if (/\b(tax id|कर पहचान|कर आईडी|الرقم الضريبي)\b/i.test(text) && /\b(email|phone|ph|address|ईमेल|फ़ोन|पता)\b/i.test(text)) {
            cards = [div];
            break;
          }
        }
      }

      // 3. Hard filter: Strip any styling if a store header or KOT block was somehow matched
      cards = cards.filter((card) => {
        const text = (card.textContent || '').toLowerCase();
        if (text.includes('gstin:') || text.includes('fssai:') || text.includes('table:') || text.includes('tax invoice') || text.includes('restaurant') || card.offsetHeight > 140) {
          card.style.removeProperty('border-left');
          card.style.removeProperty('border');
          card.style.removeProperty('background-color');
          return false;
        }
        return true;
      });

      // 4. Style ONLY the compact customer card
      cards.forEach((card) => {
        const innerCols = card.querySelectorAll('div');
        innerCols.forEach((col) => {
          const colEl = col as HTMLElement;
          colEl.style.removeProperty('border');
          colEl.style.removeProperty('border-left');
          colEl.style.removeProperty('background-color');
          colEl.style.removeProperty('border-radius');
        });

        card.style.setProperty('background-color', '#f8fafc', 'important');
        card.style.setProperty('border', '1px solid #e2e8f0', 'important');
        card.style.setProperty('border-left', `4px solid ${barColor}`, 'important');
        card.style.setProperty('border-radius', '4px', 'important');
        card.style.setProperty('padding', '6px 8px', 'important');
        card.style.setProperty('box-sizing', 'border-box', 'important');
        card.style.setProperty('font-family', 'monospace', 'important');
        card.style.setProperty('margin', '4px 0', 'important');

        // Force the card text to gray by default
        card.style.setProperty('color', '#475569', 'important');

        // ── 100% Universal Tax ID Detection (Zero Hardcoded Language Words) ──
        const buyerId = (data.buyerTaxId || '').trim();
        const dynamicTaxLabel = (
          (data as any).translatedValues?.tax_label_tr ||
          (data as any).dynamicValuesAr?.tax_label_tr ||
          (data as any).taxIdLabel ||
          'Tax ID'
        ).trim().toLowerCase().replace(/:$/, '');

        const isTaxIdText = (str: string, el?: HTMLElement) => {
          if (!str) return false;
          const s = str.trim().toLowerCase();
          // 1. Matches the Tax ID number (universal across all languages)
          if (buyerId && str.includes(buyerId)) return true;
          // 2. Matches the dynamic translation label in any language (Hindi, German, Russian, etc.)
          if (dynamicTaxLabel && s.includes(dynamicTaxLabel)) return true;
          // 3. Fallback English
          if (/tax\s*id/i.test(str)) return true;
          // 4. Element directly preceding the Tax ID number (handles multi-line: label on line 1, number on line 2)
          if (el && buyerId && el.nextElementSibling?.textContent?.includes(buyerId)) return true;
          return false;
        };

        // Case A: Non-bilingual canvas
        if (card.children.length === 0 || !card.querySelector('div')) {
          const lines = (card.textContent || '').split('\n').filter(Boolean);
          card.innerHTML = lines
            .map((line, idx) => {
              const trimmed = line.trim();
              if (idx === 0 && !trimmed.includes(':')) {
                return `<div style="font-weight: 700; color: #0f172a; font-size: 11px;">${trimmed}</div>`;
              }
              if (isTaxIdText(trimmed)) {
                return `<div style="color: #047857; font-weight: 700; font-size: 9.5px; margin-top: 1px;">${trimmed}</div>`;
              }
              return `<div style="color: #475569; font-weight: 400; font-size: 9.5px;">${trimmed}</div>`;
            })
            .join('');
        } else {
          // Case B: BilingualInvoiceRenderer already styles Name (black), Tax ID (green), and Others (gray).
          // Only enforce monospace font so DOM styles don't conflict with React:
          const leaves = card.querySelectorAll('div, span, p');
          leaves.forEach((leaf) => {
            const lEl = leaf as HTMLElement;
            if (lEl.children.length === 0) {
              lEl.style.setProperty('font-family', 'monospace', 'important');
            }
          });
        }
      });
    };

    formatCustomerCards();
    const interval = setInterval(formatCustomerCards, 150);
    return () => clearInterval(interval);
  }, [data, gestureZoom, finalElements]);

  return (
    <div
      style={{
        width: '100%',
        minHeight: '100vh',
        background: '#e5e7eb',
        overflow: 'auto',
      }}
    >
      <style>{`
        html,
        body,
        #root {
          margin: 0;
          padding: 0;
          width: 100%;
          min-height: 100%;
        }
        ${isThermal ? `
        /* Customer Card: only applied to thermal receipts */
        [data-elid="auto-injected-client-box"] {
          background-color: #f8fafc !important;
          border: 1px solid #e2e8f0 !important;
          border-radius: 4px !important;
          padding: 6px 8px !important;
          box-sizing: border-box !important;
          width: 100% !important;
          font-family: monospace !important;
          margin: 4px 0 !important;
        }
        ` : ''}
         /* Table cells: always show full text, never "..." */
.mobile-invoice-canvas .truncate,
.mobile-invoice-canvas [style*="text-overflow"] {
  text-overflow: clip !important;
  overflow: visible !important;
  overflow-wrap: normal;
  word-break: normal;
  line-height: 1.2;
}
/* Mobile only: hide CanvasWorkspace Interactive Vector Editor popup */
.mobile-invoice-canvas main > div[class*="bottom-5"] {
  display: none !important;
}
/* Mobile preview: CanvasWorkspace's internal dotted desktop must NOT
   travel with the invoice. Remove it from the moving workspace. */
.mobile-invoice-canvas main > div[class*="overflow-auto"] {
  background-image: none !important;
  background-color: transparent !important;
}
        /*
 * MOBILE PREVIEW ONLY
 * Hide CanvasWorkspace's own top rail:
 * Single / Auto / A4 Split / zoom slider / Reset
 */
.mobile-invoice-canvas main > div:first-of-type {
  display: none !important;
}

/* Strip CanvasWorkspace's own bg-slate-50 off <main> so the dotted
   pattern from .mobile-invoice-canvas shows through when panning/
   zooming past the paper's edges, instead of a flat white/gray fill. */
.mobile-invoice-canvas main {
  background: transparent !important;
}

/*
 * Keep CanvasWorkspace virtual desktop fixed.
 * Only the white invoice paper will be transformed.
 */
.mobile-invoice-canvas main > div:nth-of-type(2) {
  overflow: visible !important;
  padding: 0 !important;
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;

  /* MOBILE ONLY: CanvasWorkspace centers its canvas by default, which
     symmetrically clips top+bottom when the invoice is taller than the
     100vh preview container. Force top-left alignment instead so
     nothing is cropped — the rest is reachable via pan/zoom. */
  align-items: flex-start !important;
  justify-content: flex-start !important;
}
  /*
 * Mobile: keep CanvasWorkspace background stationary.
 * Pan is handled by the wrapper around CanvasWorkspace.
 */
.mobile-invoice-canvas main {
  transform: none !important;
}
     
      `}</style>

      <div
  className="mobile-invoice-canvas"
  onTouchStart={handleGestureStart}
  onTouchMove={handleGestureMove}
  onTouchEnd={handleGestureEnd}
  onTouchCancel={handleGestureEnd}
  style={{
  width: '100%',
  height: '100vh',
  overflow: 'hidden',
  touchAction: 'none',

  backgroundColor: '#e5e7eb',
  backgroundImage: 'none',

  ['--mobile-pan-x' as any]: `${pan.x}px`,
  ['--mobile-pan-y' as any]: `${pan.y}px`,
  ['--mobile-preview-zoom' as any]: gestureZoom,
}}
>
        <div
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${gestureZoom})`,
            transformOrigin: 'top left',
            width: isThermal ? `${cwData.pageConfig.width}px` : `${cwData.pageConfig.width}px`,
            minHeight: isThermal ? 'auto' : `${computedHeight}px`,
            backgroundColor: '#fff',
            boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
            willChange: 'transform',
          }}
        >
          {false ? null : (
            <BilingualInvoiceRenderer
              template={{
                ...template,
                // ⚡ Strip Arabic from elements (stamps, signatures) when bilingual is OFF!
                elements: (shiftedCwElements || []).map((el: any) => {
                  if (!data.bilingualMode) {
                    const { arabicContent, arabicSignatureName, arabicHeader, ...rest } = el;
                    return rest;
                  }
                  return el;
                }),
              }}
              clientName={data.clientName || ''}
              clientEmail={data.clientEmail || ''}
              clientPhone={data.clientPhone || ''}
              clientAddress={data.clientAddress || ''}
              buyerTaxId={(data as any).buyerTaxId}
              isB2B={Boolean((data as any).isB2B)}
              invoiceNumber={(data as any).invoiceNumber || 'PREVIEW'}
              invoiceDate={invoiceDate}
              invoiceTime={invoiceTime}
              notes={data.notes || ''}
              notesTranslated={data.bilingualMode ? (data.notesTranslated || '') : ''}
              cashierName={data.cashierName || (data as any).serverName || ''}
              cashierNameTranslit={data.bilingualMode ? ((data as any).cashierNameTranslit || '') : ''}
              lineItems={(data.lineItems || [])
                .filter((item) => item.description?.trim())
                .map((item) => ({
                  ...item,
                  arabicName: data.bilingualMode
                    ? (item.arabicName || item.translatedDescription || data.translatedCart?.[item.id] || '')
                    : '',
                }))}
              // ⚡ Suppress Arabic labels in totals box when bilingual is OFF
              taxLines={(data.taxLines || []).map((t: any) => ({
                ...t,
                labelAr: data.bilingualMode ? (t.labelAr || t.arabicLabel || '') : '',
                arabicLabel: data.bilingualMode ? (t.arabicLabel || t.labelAr || '') : '',
              }))}
              discountLines={(data.discountLines || []).map((d: any) => ({
                ...d,
                labelAr: data.bilingualMode ? (d.labelAr || d.arabicLabel || '') : '',
                arabicLabel: data.bilingualMode ? (d.arabicLabel || d.labelAr || '') : '',
              }))}
              autoDiscountAmount={(data as any).autoDiscountAmount || (data as any).auto_discount_amount || 0}
              autoDiscountLabel={(data as any).autoDiscountLabel || (data as any).auto_discount_label || ''}
              sym={
                data.currency === 'INR' ? '₹' :
                data.currency === 'USD' ? '$' :
                data.currency === 'EUR' ? '€' :
                data.currency === 'GBP' ? '£' :
                data.currency === 'AED' ? 'د.إ' :
                data.currency === 'SAR' ? 'ر.س' :
                data.currency || '₹'
              }
              shippingCost={Number(data.shippingCost || 0)}
              invoiceStatus={data.invoiceStatus || 'DRAFT'}
              translateTargetLang={data.bilingualMode ? (data.translateTargetLang || 'ar') : 'en'}
              isBilingual={Boolean(data.bilingualMode)}
              bilingualLayout={data.bilingualLayout || 'side'}
              isThermal={pageSize === 'THERMAL_58MM' || pageSize === 'THERMAL_80MM'}
              showAmountInWords={data.showAmountInWords ?? false}
              currencyCode={data.currency || 'INR'}
              amountInWordsTranslated={data.bilingualMode ? (data.amountInWordsTranslated || '') : ''}
              amountInWordsStyle={data.amountInWordsStyle}
              savedElements={previewQrElements}
              qrOffset={qrOffset}
              autoQrPosition={data.autoQrPosition || 'center'}
              showTrackingQR={!!data.showTrackingQR}
              // ⚡ Only show Arabic QR caption when bilingual is ON
              qrCaptionInvoiceAr={data.bilingualMode ? (data.qrCaptionInvoiceAr || (data as any).translatedValues?.qr_caption_invoice || '') : ''}
              qrCaptionTrackingAr={data.bilingualMode ? (data.qrCaptionTrackingAr || (data as any).translatedValues?.qr_caption_tracking || '') : ''}
              dynamicValues={(data as any).dynamicValues || {}}
              dynamicValuesAr={data.bilingualMode ? ((data as any).translatedValues || (data as any).dynamicValuesAr || {}) : {}}
              dynamicFieldLabelsAr={data.bilingualMode ? ((data as any).dynamicFieldLabelsAr || {}) : {}}
              taxBreakdown={(data as any).taxBreakdown}
              taxScheme={(data as any).taxScheme}
              sellerRegion={(data as any).sellerRegion}
              buyerRegion={(data as any).buyerRegion}
              taxBreakdownAr={data.bilingualMode ? (data.taxBreakdownAr || {}) : {}}
            />
          )}
        </div>
      </div>
    </div>
  );
}